import type { ReactNode } from "react";

const TOKENS = /(\*\*.+?\*\*|`[^`]+`)/g;
const TOKENS_LINKS = /(\*\*.+?\*\*|`[^`]+`|https?:\/\/[^\s<]+[^\s<.,;:)])/g;

/** The prototype's `inl()` (bold and code) and, with `links`, its `inlx()` (web addresses become links). */
export function Inline({ text, links = false }: { text: string; links?: boolean }) {
  const parts = String(text).split(links ? TOKENS_LINKS : TOKENS);
  return (
    <>
      {parts.map((p, i): ReactNode => {
        if (!p) return null;
        if (p.startsWith("**") && p.endsWith("**") && p.length > 4) return <b key={i}>{p.slice(2, -2)}</b>;
        if (p.startsWith("`") && p.endsWith("`") && p.length > 2) return <code key={i}>{p.slice(1, -1)}</code>;
        if (links && /^https?:\/\//.test(p))
          return (
            <a key={i} href={p} target="_blank" rel="noopener noreferrer">
              {p.replace(/^https?:\/\/(www\.)?/, "")}
            </a>
          );
        return p;
      })}
    </>
  );
}

const isRule = (row: string) => /^\s*\|?[\s:|-]+\|?\s*$/.test(row);
const cells = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());

/** The prototype's `rich()`: paragraphs, bullet and numbered lists, and tables, from plain markdown. */
export function Rich({ text, className = "rich", block = false }: { text: string; className?: string; block?: boolean }) {
  const out: ReactNode[] = [];
  let key = 0;
  for (const para of String(text).split(/\n\s*\n/)) {
    const lines = para.split("\n").filter((l) => l.trim());
    let ul: string[] = [];
    let ol: string[] = [];
    let tb: string[] = [];
    const flush = () => {
      if (ul.length)
        out.push(
          <ul className="bl" key={key++}>
            {ul.map((x, i) => (
              <li key={i}>
                <Inline text={x} />
              </li>
            ))}
          </ul>,
        );
      if (ol.length)
        out.push(
          <ol className="ol" key={key++}>
            {ol.map((x, i) => (
              <li key={i}>
                <Inline text={x} />
              </li>
            ))}
          </ol>,
        );
      if (tb.length) {
        const rows = tb.filter((r) => !isRule(r)).map(cells);
        out.push(
          <div className="tbw" key={key++}>
            <table className="rt">
              <thead>
                <tr>
                  {rows[0].map((c, i) => (
                    <th key={i}>
                      <Inline text={c} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(1).map((r, i) => (
                  <tr key={i}>
                    {r.map((c, j) => (
                      <td key={j}>
                        <Inline text={c} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
      }
      ul = [];
      ol = [];
      tb = [];
    };
    for (const l of lines) {
      if (/^\s*\|/.test(l)) {
        if (ul.length || ol.length) {
          const keep = tb;
          flush();
          tb = keep;
        }
        tb.push(l);
      } else if (/^\s*-\s+/.test(l)) {
        if (ol.length || tb.length) flush();
        ul.push(l.replace(/^\s*-\s+/, ""));
      } else if (/^\s*\d+\.\s+/.test(l)) {
        if (ul.length || tb.length) flush();
        ol.push(l.replace(/^\s*\d+\.\s+/, ""));
      } else {
        flush();
        out.push(
          <p key={key++}>
            <Inline text={l} />
          </p>,
        );
      }
    }
    flush();
  }
  return block ? <div className={className}>{out}</div> : <span className={className}>{out}</span>;
}
